import { defineCustomElement, getAttribute, getBooleanAttribute, getReactEventHandler, isAttrEqual, isAttrTrue, NectaryElement, updateAttribute, updateBooleanAttribute, updateExplicitBooleanAttribute } from '../utils';
const templateHTML = '<style>:host{display:inline-block;vertical-align:middle;outline:0}#wrapper{position:relative;display:flex;flex-direction:row;gap:8px;width:100%;height:var(--sinch-local-size);cursor:pointer;user-select:none;--sinch-local-size:20px}:host([small]) #wrapper{--sinch-local-size:16px}:host([disabled]) #wrapper{cursor:initial}#knob-container{position:relative;width:40px;height:var(--sinch-local-size);border-radius:calc(var(--sinch-local-size) * .5);background-color:var(--sinch-comp-toggle-color-default-background-initial);pointer-events:none;transition:background-color .15s ease-in-out}:host([small]) #knob-container{width:32px;height:var(--sinch-local-size)}:host([checked]) #knob-container{background-color:var(--sinch-comp-toggle-color-checked-background-initial)}:host([disabled]) #knob-container{background-color:var(--sinch-comp-toggle-color-disabled-background-initial)}:host([checked][disabled]) #knob-container{background-color:var(--sinch-comp-toggle-color-checked-disabled-background-initial)}#knob{position:relative;width:16px;height:16px;left:2px;top:2px;border-radius:50%;background-color:var(--sinch-comp-toggle-color-default-knob-background-initial);box-shadow:var(--sinch-comp-toggle-shadow-knob-default);transform:translateX(0);transition:transform .15s ease-in-out;will-change:transform}:host([small]) #knob{width:12px;height:12px}:host([disabled]) #knob-container>#knob{box-shadow:var(--sinch-comp-toggle-shadow-knob-disabled)}:host([checked]) #knob-container>#knob{transform:translateX(20px)}:host([small][checked]) #knob-container>#knob{transform:translateX(16px)}#knob::after,#knob::before{font:var(--sinch-comp-toggle-font-size-m-inside-text);color:var(--sinch-comp-toggle-color-default-text-inside-initial);text-transform:uppercase;position:absolute;top:0;padding:0 1px;opacity:0;transition:opacity .15s ease-in-out;user-select:none}#knob::before{content:"on";right:100%}#knob::after{content:"off";left:100%}:host([labeled]:not([small]):not([checked])) #knob::after{opacity:1}:host([labeled]:not([small])[checked]) #knob::before{opacity:1}@media (prefers-reduced-motion){#knob,#knob-container,#knob::after,#knob::before{transition:none}}#outline{display:none;position:absolute;left:-3px;top:-3px;right:-3px;bottom:-3px;border:2px solid var(--sinch-comp-toggle-color-default-outline-focus);border-radius:17px;pointer-events:none}:host(:focus-visible) #outline{display:block}#label{flex:1;min-width:0;align-self:center;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;font:var(--sinch-comp-toggle-font-size-m-label);color:var(--sinch-comp-toggle-color-default-label-initial)}#label:empty{display:none}:host([small]) #label{font:var(--sinch-comp-toggle-font-size-s-label)}:host([disabled]) #label{color:var(--sinch-comp-toggle-color-disabled-label-initial)}</style><div id="wrapper"><div id="knob-container"><div id="outline"></div><div id="knob"></div></div><span id="label"></span></div>';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-toggle', class extends NectaryElement {
  #$label;
  #controller = null;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$label = shadowRoot.querySelector('#label');
  }
  connectedCallback() {
    this.#controller = new AbortController();
    const {
      signal
    } = this.#controller;
    const options = {
      signal
    };
    this.role = 'checkbox';
    this.addEventListener('click', this.#onClick, options);
    this.addEventListener('keydown', this.#onKeydown, options);
    this.addEventListener('focus', this.#onFocus, options);
    this.addEventListener('blur', this.#onBlur, options);
    this.addEventListener('-change', this.#onChangeReactHandler, options);
    this.addEventListener('-focus', this.#onFocusReactHandler, options);
    this.addEventListener('-blur', this.#onBlurReactHandler, options);
    this.#updateTabIndex();
  }
  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['checked', 'disabled', 'text', 'labeled', 'small'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    if (isAttrEqual(oldVal, newVal)) {
      return;
    }
    switch (name) {
      case 'text':
        {
          this.#$label.textContent = newVal;
          break;
        }
      case 'checked':
        {
          const isChecked = isAttrTrue(newVal);
          updateExplicitBooleanAttribute(this, 'aria-checked', isChecked);
          updateBooleanAttribute(this, name, isChecked);
          break;
        }
      case 'disabled':
        {
          const isDisabled = isAttrTrue(newVal);
          this.#updateTabIndex();
          updateExplicitBooleanAttribute(this, 'aria-disabled', isDisabled);
          updateBooleanAttribute(this, name, isDisabled);
          break;
        }
      case 'small':
      case 'labeled':
        {
          updateBooleanAttribute(this, name, isAttrTrue(newVal));
          break;
        }
    }
  }
  set checked(isChecked) {
    updateBooleanAttribute(this, 'checked', isChecked);
  }
  get checked() {
    return getBooleanAttribute(this, 'checked');
  }
  set small(isSmall) {
    updateBooleanAttribute(this, 'small', isSmall);
  }
  get small() {
    return getBooleanAttribute(this, 'small');
  }
  set labeled(isLabeled) {
    updateBooleanAttribute(this, 'labeled', isLabeled);
  }
  get labeled() {
    return getBooleanAttribute(this, 'labeled');
  }
  set disabled(isDisabled) {
    updateBooleanAttribute(this, 'disabled', isDisabled);
  }
  get disabled() {
    return getBooleanAttribute(this, 'disabled');
  }
  set text(value) {
    updateAttribute(this, 'text', value);
  }
  get text() {
    return getAttribute(this, 'text');
  }
  get focusable() {
    return true;
  }
  #onClick = () => {
    if (this.disabled) {
      return;
    }
    this.dispatchEvent(new CustomEvent('-change', {
      detail: !this.checked
    }));
  };
  #onKeydown = e => {
    switch (e.code) {
      case 'Space':
      case 'Enter':
        {
          e.preventDefault();
          this.click();
        }
    }
  };
  #updateTabIndex() {
    this.tabIndex = this.disabled ? -1 : 0;
  }
  #onFocus = () => {
    this.dispatchEvent(new CustomEvent('-focus'));
  };
  #onBlur = () => {
    this.dispatchEvent(new CustomEvent('-blur'));
  };
  #onChangeReactHandler = e => {
    getReactEventHandler(this, 'on-change')?.(e);
  };
  #onFocusReactHandler = e => {
    getReactEventHandler(this, 'on-focus')?.(e);
  };
  #onBlurReactHandler = e => {
    getReactEventHandler(this, 'on-blur')?.(e);
  };
});